"use client";

import { BlockiesAvatar } from "./blockies-avatar";
import { QuestLogo } from "./quest-logo";
import { getUserTier, getProgressToNextTier, getTierConfig } from "@/config/tiers";
import type { TaskStatus, ChallengeCampaign } from "@/types/challenges.types";

interface UserStatsProps {
  address?: string;
  name?: string;
  totalPoints: number;
  leaderboardRank: number;
  tasks?: TaskStatus[];
  campaign?: ChallengeCampaign;
  completedTaskGuids?: string[];
}

export function UserStats({
  address,
  name,
  totalPoints,
  leaderboardRank,
  tasks,
  campaign,
  completedTaskGuids,
}: UserStatsProps) {
  const displayName =
    name ||
    (address ? `${address.slice(0, 6)}...${address.slice(-4)}` : "User");

  // Calculate user tier and progress (use completion-based if data available)
  const userTier =
    completedTaskGuids && tasks && campaign
      ? getUserTier(completedTaskGuids, tasks, campaign)
      : getUserTier(totalPoints);

  const tierProgress =
    completedTaskGuids && tasks && campaign
      ? getProgressToNextTier(completedTaskGuids, tasks, campaign, userTier)
      : getProgressToNextTier(totalPoints);

  const tierConfig = getTierConfig(userTier);

  return (
    <div className="w-full py-8 md:py-12">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        {/* Greeting with Avatar */}
        <div className="flex items-start gap-3 mb-6">
          {address && (
            <BlockiesAvatar
              seed={address}
              size={12}
              scale={4}
              className="rounded-full flex-shrink-0"
            />
          )}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-swapper-secondary mb-2">
              <span className="text-swapper-primary">Welcome,</span>{" "}
              {displayName}!
            </h2>
            <p className="text-gray-500 text-sm md:text-base">
              Complete tasks. Invite friends. Get rewarded.
            </p>
          </div>
        </div>

        {/* Stats Grid - 4 columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {/* Total Points */}
          <div className="bg-[#F1F3FF] p-6 rounded-2xl">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-swapper-secondary text-2xl md:text-3xl font-bold">
                {totalPoints.toLocaleString()}
              </p>
              <QuestLogo size={20} />
            </div>
            <p className="text-gray-500 text-sm font-medium">Total Points</p>
          </div>

          {/* Leaderboard Place */}
          <div className="bg-[#F1F3FF] p-6 rounded-2xl">
            <div className="text-swapper-secondary text-2xl md:text-3xl font-bold mb-3">
              #{leaderboardRank.toLocaleString()}
            </div>
            <p className="text-gray-500 text-sm font-medium">
              Place in Leaderboard
            </p>
          </div>

          {/* Tier Progress Bar */}
          <div className="bg-[#F1F3FF] p-6 rounded-2xl col-span-2">
            {tierProgress.nextTier ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-medium">
                    Progress to {getTierConfig(tierProgress.nextTier).displayName}
                  </span>
                  <span className="text-swapper-primary font-bold">
                    {"tasksCompletedInCurrentTier" in tierProgress
                      ? `${tierProgress.totalTasksInCurrentTier - tierProgress.tasksCompletedInCurrentTier} tasks remaining`
                      : `${(tierProgress as any).pointsToNextTier} pts needed`}
                  </span>
                </div>
                {/* Progress Bar */}
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-swapper-primary to-purple-600 transition-all duration-300 ease-out rounded-full"
                    style={{ width: `${tierProgress.progressPercentage}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-swapper-secondary font-bold">
                    {tierConfig.displayName}
                  </span>
                  <span className="text-gray-400">
                    {"tasksCompletedInCurrentTier" in tierProgress
                      ? `${tierProgress.tasksCompletedInCurrentTier} / ${tierProgress.totalTasksInCurrentTier} tasks completed`
                      : `${totalPoints.toLocaleString()} / ${getTierConfig(tierProgress.nextTier).minPoints.toLocaleString()} points`}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 py-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-swapper-primary to-purple-600 animate-pulse" />
                <span className="text-lg font-bold text-swapper-primary">
                  Ambassador
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
